import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMG, STONES } from "../lib/site";
import { Reveal, SectionHead, Eyebrow, EmberField, GoldButton } from "../components/motion";
import { Ribbon } from "../components/Ribbon";

const PIECES = [
  {
    name: "The Belleville",
    tag: "Signature Whiskey Barrel",
    price: "From $3,850 CAD",
    desc: "An authentic Kentucky bourbon barrel crowned with flamed black granite. The piece that started it all — shown here with a custom marine engraving for a Belleville client.",
    features: ["Charred American white oak", "Flamed black granite top", "65,000 BTU round burner", "Volcanic lava ember bed"],
    img: IMG.heroTable,
    testId: "piece-belleville",
  },
  {
    name: "The Vintner",
    tag: "French Wine Cask",
    price: "From $4,050 CAD",
    desc: "Wine-stained French oak with a history you can see in every stave, finished with honed limestone. Warmth and heritage for terraces, cottages and vineyard patios.",
    features: ["Authentic French wine cask", "Honed limestone top", "Wine-ring patina preserved", "Choice of fire media"],
    img: IMG.cellar,
    testId: "piece-vintner",
  },
  {
    name: "The Estate Crest",
    tag: "Bespoke Identity Edition",
    price: "From $4,900 CAD",
    desc: "Your family name, estate crest or corporate emblem laser-etched and hand-finished into the barrel itself. A piece that was never selected from a showroom — it was commissioned.",
    features: ["Full custom crest engraving", "Hermosa or Natural Slate top", "Reflective amber fireglass", "Certificate of authenticity"],
    img: IMG.branding,
    testId: "piece-estate-crest",
  },
];

export default function Collections() {
  return (
    <>
      <section className="relative overflow-hidden pb-20 pt-40 sm:pb-28">
        <EmberField count={16} />
        <div className="animate-ember-pulse absolute left-[10%] top-[20%] h-80 w-80 rounded-full bg-amber-600/10 blur-[110px]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-10">
          <Reveal>
            <Eyebrow>The Collection</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-stone-50 sm:text-5xl lg:text-6xl">
              Signature pieces,
              <span className="italic text-gold-gradient"> no two alike.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg">
              Every barrel carries its own past. Every stone is cut for its home. Browse the
              signatures — then make one entirely your own in the Atelier.
            </p>
          </Reveal>
        </div>
      </section>

      <Ribbon />

      <section data-testid="pieces-section" className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl space-y-24 px-5 sm:px-10">
          {PIECES.map((p, i) => (
            <div
              key={p.testId}
              data-testid={p.testId}
              className={`grid items-center gap-12 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <Reveal className="relative">
                <div className="animate-ember-pulse absolute -inset-4 rounded-[2rem] bg-amber-700/10 blur-3xl" />
                <div className="relative overflow-hidden rounded-[1.5rem] border border-stone-800 shadow-ember">
                  <img src={p.img} alt={p.name} className="aspect-[4/5] w-full object-cover transition-transform duration-[1600ms] hover:scale-105" />
                  <div className="vignette absolute inset-0" />
                </div>
              </Reveal>
              <div>
                <Reveal>
                  <Eyebrow>{p.tag}</Eyebrow>
                </Reveal>
                <Reveal delay={0.08}>
                  <h2 className="mt-4 font-serif text-3xl tracking-tight text-stone-50 sm:text-4xl">{p.name}</h2>
                </Reveal>
                <Reveal delay={0.14}>
                  <p className="mt-3 font-mono text-sm tracking-wider text-amber-400">{p.price}</p>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="mt-5 text-base leading-relaxed text-stone-400 sm:text-lg">{p.desc}</p>
                </Reveal>
                <Reveal delay={0.26}>
                  <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm text-stone-300">
                        <span className="h-1.5 w-1.5 rotate-45 bg-amber-500" /> {f}
                      </li>
                    ))}
                  </ul>
                </Reveal>
                <Reveal delay={0.32}>
                  <Link to="/configurator" className="mt-9 inline-block">
                    <GoldButton data-testid={`${p.testId}-cta`}>Commission This Piece <ArrowRight size={15} /></GoldButton>
                  </Link>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="stone-library" className="border-t border-amber-900/20 bg-[#0c0a08] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <SectionHead
            eyebrow="The Stone Library"
            title="Six architectural surfaces. One is yours."
            copy="Granite, limestone, slate and rare Hermosa — each top is precision water-jet cut and hand-finished to complement the property it will live on."
          />
          <div className="mt-14 grid items-start gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <Reveal className="relative">
              <div className="overflow-hidden rounded-[1.5rem] border border-stone-800 bg-white shadow-ember">
                <img src={IMG.swatches} alt="Cozy Ember stone tabletop swatches" className="w-full object-cover" />
              </div>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-500">
                The actual Cozy Ember tabletop library — photographed in the workshop.
              </p>
            </Reveal>
            <div className="grid gap-4 sm:grid-cols-2">
              {STONES.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.07}>
                  <div
                    data-testid={`stone-card-${s.id}`}
                    className="group flex items-start gap-4 rounded-xl border border-stone-800 bg-[#141110] p-5 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-amber-500/40"
                  >
                    <span className="stone-swatch mt-0.5 h-11 w-11 shrink-0 rounded-full border border-white/10" style={{ background: s.swatch }} />
                    <div>
                      <h3 className="font-serif text-base text-stone-100">{s.name}</h3>
                      <p className="mt-1.5 text-xs leading-relaxed text-stone-400">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="flame-detail" className="relative overflow-hidden py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-10 lg:grid-cols-2">
          <div>
            <SectionHead
              eyebrow="The Flame"
              title="65,000 BTU of clean, golden theatre."
              copy="A stainless jet burner set into natural stone, dressed in volcanic lava rock. No smoke, no sparks, no compromise — only atmosphere, on command."
            />
            <Reveal delay={0.25}>
              <div className="mt-9 flex gap-10">
                <div>
                  <p className="font-serif text-3xl text-gold-gradient">65k</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">BTU Output</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-gold-gradient">CSA</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">Compliant Assembly</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-gold-gradient">20lb</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">Hidden Propane Tank</p>
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="animate-ember-pulse absolute -inset-4 rounded-[2rem] bg-amber-600/15 blur-3xl" />
            <div className="relative overflow-hidden rounded-[1.5rem] border border-amber-500/25 shadow-ember-lg">
              <img src={IMG.flame} alt="Close-up of flame over volcanic lava rock on stone" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
